import React, { useState } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import Pressable from '@atlaskit/primitives/pressable';
import CrossIcon from '@atlaskit/icon/core/cross';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';

import { usePageSpaceKey } from '@confluence/page-context';
import { isPersonalSpace } from '@confluence/space-utils/entry-points/isPersonalSpace.ts';

import { StackedContentTypeIcons } from './StackedContentTypeIcons.tsx';
import { ThirdPartyAuthenticationModal } from './ThirdPartyAuthenticationModal.tsx';

import googleDriveIcon from './img/google-drive.svg';
import microsoftTeamsIcon from './img/microsoft-teams.svg';
import loomIcon from './img/loom.svg';

const THIRD_PARTY_ICON_URLS = [googleDriveIcon, microsoftTeamsIcon, loomIcon];

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
		marginTop: token('space.100'),
		overflow: 'hidden',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	mainAction: {
		flexGrow: 1,
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.100'),
		backgroundColor: 'transparent',
	},
	iconsWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	dismissButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: token('space.050'),
		backgroundColor: 'transparent',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects token values; transparent needed to inherit container hover
			backgroundColor: 'transparent',
		},
	},
});

const i18n = defineMessages({
	addContent: {
		id: 'side-navigation.space-navigation.third-party-auth.connect-your-apps',
		defaultMessage: 'Connect your apps',
		description: 'Label for the pill button that opens the content suggestions modal',
	},
	dismiss: {
		id: 'side-navigation.space-navigation.third-party-auth.dismiss',
		defaultMessage: 'Dismiss related content',
		description: 'Accessible label for the button to dismiss the related content panel',
	},
});

export const ThirdPartyAuthentication = () => {
	const intl = useIntl();
	const [isVisible, setIsVisible] = useState(true);
	const [isModalOpen] = useState(false);
	const [spaceKey] = usePageSpaceKey();

	if (isPersonalSpace(spaceKey) || !isVisible) {
		return null;
	}

	return (
		<>
			<Box xcss={styles.container} testId="space-navigation-third-party-auth">
				<Flex alignItems="center">
					<Pressable xcss={styles.mainAction}>
						<Box xcss={styles.iconsWrapper}>
							<StackedContentTypeIcons iconUrls={THIRD_PARTY_ICON_URLS} />
						</Box>
						<Text weight="medium">{intl.formatMessage(i18n.addContent)}</Text>
					</Pressable>
					<Pressable
						xcss={styles.dismissButton}
						onClick={() => setIsVisible(false)}
						aria-label={intl.formatMessage(i18n.dismiss)}
					>
						<CrossIcon label="" />
					</Pressable>
				</Flex>
			</Box>
			<ModalTransition>{isModalOpen && <ThirdPartyAuthenticationModal />}</ModalTransition>
		</>
	);
};
